import React, { useRef, useState, useEffect } from "react";
import ReplayIcon from "@mui/icons-material/Replay";
import FilterAltOutlined from "@mui/icons-material/FilterAltOutlined";
import {
  TextField,
  IconButton,
  Button,
  List,
  ListItemText,
  ListItemButton,
  Popover,
  ListItemIcon,
  Chip,
  Badge,
  Divider,
  Box,
  Typography,
  InputBase,
  Tooltip,
  MenuItem,
  Select,
  Autocomplete,
  Checkbox,
  Card,
  CircularProgress,
} from "@mui/material";
import {
  Search,
  MoreVert,
  ArrowDropDown,
  DeleteOutline,
  FileDownload,
  SwapVert,
  ArrowUpward,
  ArrowDownward,
  DragIndicator,
  Add,
  Close,
  ViewWeek,
  RadioButtonChecked,
  RadioButtonUnchecked,
} from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import { tooltipStyleProps } from "services/constants/Styles";
import { showToast } from "services/utils/Status";
import { useFilter } from "pages/FilterContext";
import {
  useExcelDownload,
  MAX_PDF_ROWS,
  MAX_PDF_COLUMNS,
  getFilteredHeaders,
} from "./useExcelDownload";
import { themes } from "services/constants";
import { designColors } from "services/constants/designTokens";
import { DeviceSelect } from "./StandardListControls";
import { useServerCustomFilter } from "./useServerCustomFilter";
import { downloadStandardExport } from "./StandardExportButton";
import { filterToolbarButtonSx, filterToolbarSearchSx } from "./filterToolbarStyles";
import { filterFieldIcons } from "./filterFieldIcons";

// Screens often build metadata once on page load; stamp the download time instead.
const GENERATED_ON_KEY = "Report Generated On";
export const withFreshGeneratedOn = (metadata) => {
  if (!metadata || !(GENERATED_ON_KEY in metadata)) return metadata;
  const format = /^\d{2}-\d{2}-\d{4}/.test(String(metadata[GENERATED_ON_KEY])) ? "DD-MM-YYYY HH:mm:ss" : "DD/MM/YYYY HH:mm:ss";
  return { ...metadata, [GENERATED_ON_KEY]: dayjs().format(format) };
};

const DETAILED_DOWNLOAD_PROGRESS_KEY = "teamzone_detailed_download_progress";

// Server-backed lists share the toolbar GUI without running client-side filters.
export const CustomFilter = ({ serverTable, serverConfig, serverExport, ...props }) =>
  serverTable ? (
    <ServerCustomFilter {...props} table={serverTable} config={serverConfig} exportConfig={serverExport} />
  ) : <ClientCustomFilter {...props} />;

function ServerCustomFilter({ table, config, exportConfig, hideDefaultDateChip = false, pickerDateField, ...props }) {
  const preferences = useFilter();
  const filterState = useServerCustomFilter(table, config, preferences);
  const writers = useExcelDownload({ maxPdfRows: 5000 });
  const request = useRef(null);
  useEffect(() => () => request.current?.abort(), []);
  const download = async (_, progress, format = "excel") => {
    if (request.current) return;
    const controller = new AbortController(); request.current = controller;
    try {
      await downloadStandardExport({ ...exportConfig, query: table.queryString, format, writers, signal: controller.signal,
        filterLabels: config.labels, sortOptions: config.sortOptions, onProgress: progress });
    } catch (error) {
      if (!controller.signal.aborted) showToast("error", error.message || "Could not export records.");
    } finally { if (request.current === controller) request.current = null; }
  };
  const dateField = config.defaultDateField || "event_time";
  const defaultApplied = table.data?.query?.defaults_applied?.includes(dateField);
  const extraChips = <>
    {!hideDefaultDateChip && defaultApplied && !table.state[`${dateField}_from`] && !table.state[`${dateField}_to`] && table.state[dateField] !== "all" &&
      <Chip label="Date: Last 7 days (default)" onDelete={() => table.setParams({ [dateField]: "all" })} sx={{ borderRadius: "6px" }} />}
    {!hideDefaultDateChip && table.state[dateField] === "all" && <Chip label="Event time: All dates" onDelete={() => table.setParams({ [dateField]: null })} />}
  </>;
  return <ClientCustomFilter {...props} serverTable={table} serverConfig={config} filterState={filterState}
    title={exportConfig.filename} filterHeaders={exportConfig.headers} data={props.data || []} filteredData={props.data || []}
    filterableHeaders={filterState.headers} sortableHeaders={config.sortOptions.map(option => ({ key: option.value, name: option.label, icon: option.icon }))}
    labeledToolbar screenAccess hideCount extraFilterChips={extraChips} pickerDateField={pickerDateField || (hideDefaultDateChip ? dateField : null)}
    toolbarActions={<Tooltip title="Refresh" arrow slotProps={tooltipStyleProps}>
      <span><IconButton aria-label="Refresh events" onClick={table.refresh} disabled={table.inFlight}><ReplayIcon /></IconButton></span>
    </Tooltip>}
    searchPlaceholder="Search events..." downloadFormats={["excel", "pdf", "csv"]}
    pdfRowCountOverride={table.data?.pagination?.count_is_estimate ? null : table.data?.pagination?.total_count}
    pdfMaxRows={5000} customDownloadHandler={download} />;
}

const ClientCustomFilter = ({
  serverTable,
  serverConfig,
  filterState,
  pickerDateField = null,
  extraFilterChips,
  pdfMaxRows = MAX_PDF_ROWS,
  title,
  titleOverride = null,
  filterHeaders = [],
  // Optional narrower list of headers offered in the "Add Filter" popover.
  // Search, sort, and export still use the full filterHeaders — this only
  // restricts which fields a user can filter by. Defaults to filterHeaders
  // so every existing screen is unaffected.
  filterableHeaders = null,
  // Same idea for the "Add Sort" field picker. Defaults to filterHeaders.
  sortableHeaders = null,
  data = [],
  onFilteredDataChange,
  newButtonComponent,
  defaultDateFilter,
  screenAccess,
  filteredData = [],
  filteredDataReady = true,
  showSaveButton = true,
  showReprocessButton = false,
  setShowReprocessDialog,
  showtitle_count = true,
  viewFilter = true,
  showChip = true,
  showFilter = false,
  filters,
  showDownloadOptions = false,
  onRequestDetailedData,
  onRequestSummarizedData,
  getDetailedHeaders,
  customSummarizedDownloadHandler,
  customDetailedDownloadHandler,
  downloadFormats = ["excel"],
  includeMetadata = false,
  metadata = {},
  hideCount = false,
  hideDownloadButton = false,
  customDownloadHandler,
  showDownloadTypeSelector = false,
  downloadOptionStates = null,
  downloadDisabledReason = "",
  pdfRowCountOverride = null,
  pdfColumnCountOverride = null,
  showSavedViewControl = false,
  savedViews = [],
  activeSavedViewId = null,
  savedViewHeaders = [],
  onCreateSavedView,
  onSelectSavedView,
  onDeleteSavedView,
  showSavedViewChipsInFilterRow = true,
  // Optional custom action(s) rendered at the start of the right-hand toolbar
  // icon group (before Search). Defaults to nothing so screens that don't pass
  // it are unaffected. Used by the OT report for its "expand all" toggle.
  toolbarActions = null,
  // Opt-in toolbar redesign: Search/Sort/Download/Filters render as labeled
  // buttons (text + icon) instead of icon-only controls. Defaults to false so
  // every existing screen is unaffected until it explicitly opts in — this is
  // the staged-rollout flag: Leave turns it on first, other screens flip it
  // on individually once the look is approved.
  labeledToolbar = false,
  searchPlaceholder = "Search Sessions",
  hideToolbarDivider = false,
  // Optional controlled search for lists that search on the server (e.g.
  // StatutoryList's server mode): the box shows searchValue, typing calls
  // onSearchChange, and rows are not searched again in the browser.
  searchValue,
  onSearchChange,
}) => {
  const addFilterHeaders = filterableHeaders || filterHeaders;
  const addSortHeaders = sortableHeaders || filterHeaders;
  const { setValue } = useForm({ mode: "onBlur" });

  const contextFilterState = useFilter();
  const {
    selectedFilters,
    filterValues,
    activeFilter,
    anchor,
    setSelectedFilters,
    setFilterValues,
    setAnchor,
    setActiveFilter,
    updateFilterValues,
    savePreferences,
    isSavingPreferences,
    handleResetFilters,
    sortRules,
    addSortRule,
    removeSortRule,
    setSortDirection,
    reorderSortRules,
    applySortRules,
  } = filterState || contextFilterState;

  const cardField = serverConfig?.cardField || "status";
  const statusShownByCard = serverTable && serverConfig?.cards?.some(card =>
    card.value !== "ALL" && card.value === serverTable.state[cardField]);
  const chipFilters = selectedFilters.filter(filter => filter.key !== pickerDateField &&
    !(statusShownByCard && filter.key === cardField));

  const [open, setOpen] = useState(false);
  const [localQuery, setLocalQuery] = useState("");
  const query = serverTable ? serverTable.search : onSearchChange ? searchValue ?? "" : localQuery;
  const setQuery = serverTable ? serverTable.setSearch : onSearchChange || setLocalQuery;
  const [anchorEl, setAnchorEl] = useState(null);
  const [filteredName, setFilteredName] = useState("");
  const [menuAnchor, setMenuAnchor] = useState(null);

  // Sort panel state
  const [sortOpen, setSortOpen] = useState(false);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [sortSearchQuery, setSortSearchQuery] = useState("");
  const [addSortMenuAnchor, setAddSortMenuAnchor] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [saveViewAnchorEl, setSaveViewAnchorEl] = useState(null);
  const [savedViewName, setSavedViewName] = useState("");
  const [selectedSavedViewHeaders, setSelectedSavedViewHeaders] = useState([]);
  const [isCustomDownloadInProgress, setIsCustomDownloadInProgress] =
    useState(false);
  const [customDownloadProgress, setCustomDownloadProgress] = useState(0);

  // Ref for chip row — sort popover anchors here
  const chipRowRef = useRef(null);

  const openFilter = Boolean(anchorEl);
  const openIndividualFilter = Boolean(anchor);
  const openAddSort = Boolean(addSortMenuAnchor);
  const openSaveViewPopover = Boolean(saveViewAnchorEl);

  const { downloadCsv, downloadExcel, downloadPdf } = useExcelDownload();

  const iconButtonStyle = {
    backgroundColor: "action.hover",
    borderRadius: "50%",
    border: "1px solid",
    borderColor: "divider",
    color: "text.primary",
    opacity: 1,
    transition: "none",
    "&.Mui-disabled": {
      backgroundColor: "action.hover",
      color: "text.primary",
      opacity: 1,
    },
    "&:hover": { backgroundColor: "action.hover" },
  };

  // Labeled (text + icon) toolbar button style — used only when
  // labeledToolbar is true, matching the outlined-pill buttons already used
  // elsewhere in the redesigned screens (e.g. Leave's date-range button).
  const labeledButtonSx = filterToolbarButtonSx;

  const getHeaderOptionIcon = (icon) => {
    const FieldIcon = typeof icon === "string" ? filterFieldIcons[icon] : null;
    return FieldIcon ? <FieldIcon fontSize="small" sx={{ color: "text.secondary" }} />
      : icon || <DragIndicator fontSize="small" sx={{ color: "text.secondary" }} />;
  };

  // ── Filter + Sort effect ──────────────────────────────────────────────────
  useEffect(() => {
    if (serverTable) return;
    let filtered = data;

    if (selectedFilters.length > 0) {
      filtered = filtered.filter((row) =>
        selectedFilters.every((filter) => {
          if (filter.Viewmode) return true;
          const value = filterValues[filter.key];
          if (!value) return true;
          const rowValue = row[filter.key];
          const fieldKey = filter.key;

          switch (filter.type) {
            case "string": {
              const searchValue = value?.toLowerCase() || "";
              if (fieldKey === "start_day") {
                const dateOfMonth =
                  row?.date_of_month?.toString().toLowerCase() || "";
                const startDay = row?.start_day?.toString().toLowerCase() || "";
                return (
                  dateOfMonth.includes(searchValue) ||
                  startDay.includes(searchValue)
                );
              }
              if (Array.isArray(rowValue))
                return rowValue.some((item) =>
                  item?.toString().toLowerCase().includes(searchValue),
                );
              return rowValue?.toString().toLowerCase().includes(searchValue);
            }
            case "number": {
              if (!value) return true;
              if (typeof value !== "object") {
                const filterNum = parseFloat(value);
                const rowNum = parseFloat(rowValue);
                return !isNaN(filterNum) && !isNaN(rowNum)
                  ? rowNum === filterNum
                  : false;
              }
              const filterNumber =
                value.number !== undefined && value.number !== ""
                  ? parseFloat(value.number)
                  : null;
              const rowNumber = parseFloat(rowValue);
              if (isNaN(rowNumber)) return false;
              if (filterNumber === null || isNaN(filterNumber)) {
                if (value.operator === "between") {
                  const fromNum =
                    value.fromNumber !== undefined && value.fromNumber !== ""
                      ? parseFloat(value.fromNumber)
                      : null;
                  const toNum =
                    value.toNumber !== undefined && value.toNumber !== ""
                      ? parseFloat(value.toNumber)
                      : null;
                  let isValid = true;
                  if (fromNum !== null && !isNaN(fromNum))
                    isValid = isValid && rowNumber >= fromNum;
                  if (toNum !== null && !isNaN(toNum))
                    isValid = isValid && rowNumber <= toNum;
                  return isValid;
                }
                return true;
              }
              switch (value.operator) {
                case "equals":
                  return rowNumber === filterNumber;
                case "notEquals":
                  return rowNumber !== filterNumber;
                case "greaterEquals":
                  return rowNumber >= filterNumber;
                case "lessEquals":
                  return rowNumber <= filterNumber;
                case "between": {
                  const fromNum =
                    value.fromNumber !== undefined && value.fromNumber !== ""
                      ? parseFloat(value.fromNumber)
                      : null;
                  const toNum =
                    value.toNumber !== undefined && value.toNumber !== ""
                      ? parseFloat(value.toNumber)
                      : null;
                  let isValid = true;
                  if (fromNum !== null && !isNaN(fromNum))
                    isValid = isValid && rowNumber >= fromNum;
                  if (toNum !== null && !isNaN(toNum))
                    isValid = isValid && rowNumber <= toNum;
                  return isValid;
                }
                default:
                  return rowNumber === filterNumber;
              }
            }
            case "boolean":
              if (value === "true") return rowValue === true;
              if (value === "false") return rowValue === false;
              return true;
            case "date": {
              if (!value) return true;
              try {
                const parseDMY = (dateString) => {
                  if (!dateString) return null;
                  if (dateString instanceof Date) return dateString;
                  const normalized = dateString.toString().trim();
                  const dmyMatch = normalized.match(
                    /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/,
                  );
                  if (dmyMatch) {
                    const [, day, month, year] = dmyMatch;
                    return new Date(year, month - 1, day);
                  }
                  const ymdMatch = normalized.match(
                    /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/,
                  );
                  if (ymdMatch) {
                    const [, year, month, day] = ymdMatch;
                    return new Date(year, month - 1, day);
                  }
                  return new Date(normalized);
                };
                if (
                  (value.operator === "equals" ||
                    value.operator === "greater" ||
                    value.operator === "less") &&
                  value.date
                ) {
                  const filterDate = parseDMY(value.date);
                  if (!rowValue) return false;
                  const rowDate = parseDMY(rowValue);
                  if (
                    !filterDate ||
                    !rowDate ||
                    isNaN(filterDate.getTime()) ||
                    isNaN(rowDate.getTime())
                  )
                    return false;
                  switch (value.operator) {
                    case "equals":
                      return (
                        rowDate.toDateString() === filterDate.toDateString()
                      );
                    case "greater":
                      return rowDate >= filterDate;
                    case "less":
                      return rowDate <= filterDate;
                    default:
                      return false;
                  }
                }
                if (
                  value.operator === "between" &&
                  (value.fromDate || value.toDate)
                ) {
                  const fromDate = value.fromDate
                    ? parseDMY(value.fromDate)
                    : null;
                  const toDate = value.toDate ? parseDMY(value.toDate) : null;
                  const currentField = filterHeaders.find(
                    (f) => f.key === fieldKey,
                  );
                  if (currentField?.dateRangeFields) {
                    const { fromField, toField } = currentField.dateRangeFields;
                    if (!row[fromField] || !row[toField]) return false;
                    const recordFrom = parseDMY(row[fromField]);
                    const recordTo = parseDMY(row[toField]);
                    if (
                      !recordFrom ||
                      !recordTo ||
                      isNaN(recordFrom.getTime()) ||
                      isNaN(recordTo.getTime())
                    )
                      return false;
                    let isValid = true;
                    if (fromDate) {
                      const s = new Date(fromDate);
                      s.setHours(0, 0, 0, 0);
                      isValid = isValid && recordFrom >= s;
                    }
                    if (toDate) {
                      const e = new Date(toDate);
                      e.setHours(23, 59, 59, 999);
                      isValid = isValid && recordTo <= e;
                    }
                    return isValid;
                  }
                  if (!rowValue) return false;
                  const rowDate = parseDMY(rowValue);
                  if (!rowDate || isNaN(rowDate.getTime())) return false;
                  let isValid = true;
                  if (fromDate) {
                    const s = new Date(fromDate);
                    s.setHours(0, 0, 0, 0);
                    isValid = isValid && rowDate >= s;
                  }
                  if (toDate) {
                    const e = new Date(toDate);
                    e.setHours(23, 59, 59, 999);
                    isValid = isValid && rowDate <= e;
                  }
                  return isValid;
                }
                return true;
              } catch {
                showToast("error", "Date filtering error");
                return true;
              }
            }
            case "autocomplete": {
              if (
                !value ||
                value === "" ||
                (Array.isArray(value) && value.includes("All"))
              )
                return true;
              if (Array.isArray(value))
                return value.some(
                  (sel) =>
                    rowValue?.toString().toLowerCase() ===
                    sel.toString().toLowerCase(),
                );
              return (
                rowValue?.toString().toLowerCase() ===
                value.toString().toLowerCase()
              );
            }
            case "time": {
              if (!value) return true;
              try {
                const toMin = (t) => {
                  if (!t) return null;
                  const [h, m] = t.split(":").map(Number);
                  return (h || 0) * 60 + (m || 0);
                };
                if (
                  (value.operator === "equals" ||
                    value.operator === "greater" ||
                    value.operator === "less") &&
                  value.time
                ) {
                  const ft = toMin(value.time),
                    rt = toMin(rowValue);
                  if (ft === null || rt === null) return false;
                  switch (value.operator) {
                    case "equals":
                      return rt === ft;
                    case "greater":
                      return rt >= ft;
                    case "less":
                      return rt <= ft;
                    default:
                      return false;
                  }
                }
                if (
                  value.operator === "between" &&
                  (value.fromTime || value.toTime)
                ) {
                  const fromTime = value.fromTime
                    ? toMin(value.fromTime)
                    : null;
                  const toTime = value.toTime ? toMin(value.toTime) : null;
                  const rs = toMin(row.start_time),
                    re = toMin(row.end_time);
                  if (!rs || !re) return false;
                  const isRecordON = re < rs,
                    isFilterON = toTime < fromTime;
                  let isValid = true;
                  if (isFilterON) {
                    if (isRecordON) {
                      const rea = re + 1440,
                        tta = toTime + 1440;
                      if (fromTime)
                        isValid = isValid && rs >= fromTime && rs <= tta;
                      if (toTime)
                        isValid = isValid && rea >= fromTime && rea <= tta;
                    } else {
                      isValid =
                        isValid &&
                        ((rs >= fromTime && re <= 1440) ||
                          (rs >= 0 && re <= toTime));
                    }
                  } else {
                    if (isRecordON) return false;
                    if (fromTime) isValid = isValid && rs >= fromTime;
                    if (toTime) isValid = isValid && re <= toTime;
                  }
                  return isValid;
                }
                return true;
              } catch (error) {
                console.error('[Customfilter] operation failed:', error);
                showToast("error", error);
                return true;
              }
            }
            default:
              return true;
          }
        }),
      );
    }

    const getNestedValue = (obj, keyPath) =>
      keyPath.split(".").reduce((acc, key) => acc?.[key], obj);
    if (query?.trim() && !onSearchChange) {
      const lowerQuery = query.toLowerCase();
      const searchKeys = filterHeaders.map((h) => h.key);
      filtered = filtered.filter((item) =>
        searchKeys.some((key) => {
          const value = getNestedValue(item, key);
          return value?.toString().toLowerCase().includes(lowerQuery);
        }),
      );
    }

    filtered = applySortRules(filtered);
    onFilteredDataChange(filtered);
  }, [
    selectedFilters,
    filterValues,
    data,
    query,
    onFilteredDataChange,
    filterHeaders,
    applySortRules,
    serverTable,
    onSearchChange,
  ]);

  // Default date filter
  useEffect(() => {
    if (defaultDateFilter) {
      const dateFilterExists = selectedFilters.some((f) => f.key === "date");
      if (!dateFilterExists) {
        const dateFilterHeader = filterHeaders.find((f) => f.key === "date");
        if (dateFilterHeader) {
          setSelectedFilters((prev) => [...prev, dateFilterHeader]);
          setFilterValues((prev) => ({ ...prev, date: defaultDateFilter }));
        }
      } else if (filterValues.date === undefined || filterValues.date === "") {
        setFilterValues((prev) => ({ ...prev, date: defaultDateFilter }));
      }
    }
  }, [
    defaultDateFilter,
    filterHeaders,
    selectedFilters,
    setFilterValues,
    setSelectedFilters,
    filterValues,
  ]);

  // ── Filter handlers ───────────────────────────────────────────────────────
  const handleClick = (event) =>
    setAnchorEl(anchorEl ? null : event.currentTarget);

  const handleOpen = (event, filter) => {
    setAnchor(event.currentTarget);
    setActiveFilter(filter);
  };
  const handleClose = () => {
    if (serverTable) filterState.cancelFilters();
    else setAnchor(null);
  };
  const handleMenuOpen = (event) => setMenuAnchor(event.currentTarget);
  const handleMenuClose = () => setMenuAnchor(null);
  const handleDeleteFilter = () => {
    if (activeFilter) {
      if (serverTable) filterState.removeFilter(activeFilter.key);
      setSelectedFilters((prev) =>
        prev.filter((f) => f.key !== activeFilter.key),
      );
      setFilterValues((prev) => {
        const copy = { ...prev };
        delete copy[activeFilter.key];
        return copy;
      });
    }
    setMenuAnchor(null);
    setAnchor(null);
  };

  // ── Sort handlers ─────────────────────────────────────────────────────────
  // Both the toolbar icon and the summary chip open the popover anchored to the chip row
  const openSortPanel = () => {
    setSortAnchorEl(chipRowRef.current);
    setSortOpen(true);
  };

  const handleSortPanelClose = () => {
    setSortOpen(false);
    setSortAnchorEl(null);
    setSortSearchQuery("");
  };

  const handleOpenAddSortMenu = (event) => {
    setAddSortMenuAnchor(event.currentTarget);
    setSortOpen(true);
    setSortAnchorEl(chipRowRef.current);
    setSortSearchQuery("");
  };
  const handleCloseAddSortMenu = () => {
    setAddSortMenuAnchor(null);
    setSortSearchQuery("");
  };

  const availableSortHeaders = addSortHeaders.filter(
    (h) =>
      h?.key &&
      h?.name &&
      h.name.trim() !== "" &&
      !sortRules.some((r) => r.key === h.key),
  );

  const handleDragStart = (index) => setDragIndex(index);
  const handleDragOver = (event, index) => {
    event.preventDefault();
    if (dragIndex !== null && dragIndex !== index) {
      reorderSortRules(dragIndex, index);
      setDragIndex(index);
    }
  };
  const handleDragEnd = () => setDragIndex(null);

  // ── Dynamic options ───────────────────────────────────────────────────────
  const getDynamicOptions = (filterKey) => {
    if (!data || data.length === 0) return [];
    return [...new Set(data.map((item) => item[filterKey]))]
      .filter(Boolean)
      .sort();
  };
  const getCurrentValue = (value) => {
    if (!value || value === "" || (Array.isArray(value) && value.length === 0))
      return ["All"];
    if (Array.isArray(value)) return value;
    return [value];
  };
  const handleExcelDownload = async () => {
    const exportBaseData =
      Array.isArray(effectiveFilteredData) && effectiveFilteredData.length > 0
        ? effectiveFilteredData
        : data;

    if (customDownloadHandler) {
      if (isCustomDownloadInProgress) return;

      setIsCustomDownloadInProgress(true);
      setCustomDownloadProgress(0);
      try {
        await customDownloadHandler(exportBaseData, (progressValue) => {
          const safeProgress = Math.min(
            100,
            Math.max(0, Number(progressValue) || 0),
          );
          setCustomDownloadProgress(safeProgress);
        });
      } finally {
        setIsCustomDownloadInProgress(false);
        setCustomDownloadProgress(0);
      }
      return;
    }

    const exportHeaders =
      showDownloadOptions && getDetailedHeaders
        ? getDetailedHeaders()
        : filterHeaders;

    downloadExcel(
      exportBaseData,
      exportHeaders,
      title,
      filterValues,
      includeMetadata ? withFreshGeneratedOn(metadata) : null,
      sortRules,
    );
  };

  const handleFormatDownload = async (downloadFormat) => {
    handleDownloadClose();

    if (!screenAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to download report",
      );
      return;
    }

    const exportBaseData =
      Array.isArray(effectiveFilteredData) && effectiveFilteredData.length > 0
        ? effectiveFilteredData
        : data;

    const exportHeaders =
      showDownloadOptions && getDetailedHeaders
        ? getDetailedHeaders()
        : filterHeaders;

    if (customDownloadHandler) {
      if (isDetailedDownloadInProgress) return;

      updateDetailedDownloadProgressState(true, 0);
      await new Promise((resolve) => setTimeout(resolve, 50));
      try {
        await customDownloadHandler(
          exportBaseData,
          (progressValue) => {
            updateDetailedDownloadProgressState(true, progressValue);
          },
          downloadFormat,
        );
      } finally {
        updateDetailedDownloadProgressState(false, 0);
      }
      return;
    }

    if (isDetailedDownloadInProgress) return;

    updateDetailedDownloadProgressState(true, 0, true);
    await new Promise((resolve) => setTimeout(resolve, 50));

    const handleExportProgress = (progressValue) => {
      updateDetailedDownloadProgressState(true, progressValue, true);
    };

    try {
      if (downloadFormat === "pdf") {
        await downloadPdf(
          exportBaseData,
          exportHeaders,
          title,
          filterValues,
          includeMetadata ? withFreshGeneratedOn(metadata) : null,
          sortRules,
          handleExportProgress,
        );
      } else if (downloadFormat === "csv") {
        await downloadCsv(
          exportBaseData,
          exportHeaders,
          title,
          filterValues,
          includeMetadata ? withFreshGeneratedOn(metadata) : null,
          sortRules,
          handleExportProgress,
        );
      } else {
        await downloadExcel(
          exportBaseData,
          exportHeaders,
          title,
          filterValues,
          includeMetadata ? withFreshGeneratedOn(metadata) : null,
          sortRules,
          handleExportProgress,
        );
      }
    } finally {
      updateDetailedDownloadProgressState(false, 0, true);
    }
  };
  const handleOpenSaveViewPopover = (event) => {
    setSelectedSavedViewHeaders(savedViewHeaders.map((header) => header.key));
    setSavedViewName("");
    setSaveViewAnchorEl(event.currentTarget);
  };

  const handleCloseSaveViewPopover = () => {
    setSaveViewAnchorEl(null);
    setSavedViewName("");
    setSelectedSavedViewHeaders([]);
  };

  const toggleSavedViewHeader = (headerKey) => {
    setSelectedSavedViewHeaders((prev) =>
      prev.includes(headerKey)
        ? prev.filter((key) => key !== headerKey)
        : [...prev, headerKey],
    );
  };

  const handleSaveHeaderView = () => {
    const trimmedName = savedViewName.trim();

    if (!trimmedName) {
      showToast("error", "Please enter a view name.");
      return;
    }

    if (selectedSavedViewHeaders.length === 0) {
      showToast("error", "Select at least one header.");
      return;
    }

    onCreateSavedView?.(trimmedName, selectedSavedViewHeaders);
    handleCloseSaveViewPopover();
  };
  const handleMultipleSelection = (newValue, filterKey) => {
    const filter = selectedFilters.find((f) => f.key === filterKey);
    if (filter?.Viewmode) {
      setFilterValues((prev) => ({
        ...prev,
        [filterKey]: [newValue.length > 0 ? newValue[0] : "Group View"],
      }));
      return;
    }
    const lastSelected = newValue[newValue.length - 1];
    if (lastSelected === "All") {
      setFilterValues((prev) => ({ ...prev, [filterKey]: ["All"] }));
    } else if (newValue.includes("All") && lastSelected !== "All") {
      setFilterValues((prev) => ({
        ...prev,
        [filterKey]: newValue.filter((opt) => opt !== "All"),
      }));
    } else {
      setFilterValues((prev) => ({
        ...prev,
        [filterKey]: newValue.length > 0 ? newValue : "",
      }));
    }
  };

  // ── Render filter inputs ──────────────────────────────────────────────────
  const renderFilterInput = (filter) => {
    const value = filterValues[filter.key] || "";
    if (serverTable && filter.type === "device") {
      return <DeviceSelect endpoint={serverConfig.deviceEndpoint} value={value}
        onChange={next => setFilterValues(previous => ({ ...previous, [filter.key]: next }))} />;
    }
    if (filter.Viewmode) {
      const options = filter.options || ["Group View", "Employee View"];
      return (
        <Autocomplete
          multiple={false}
          fullWidth
          size="small"
          options={options}
          value={value}
          onChange={(e, nv) =>
            setFilterValues((prev) => ({
              ...prev,
              [filter.key]: nv ? [nv] : ["Group View"],
            }))
          }
          getOptionLabel={(o) => o}
          renderOption={(props, option) => (
            <li {...props}>
              <Typography>{option}</Typography>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder="Select view"
            />
          )}
        />
      );
    }
    switch (filter.type) {
      case "string":
        return (
          <TextField
            fullWidth
            size="small"
            placeholder="Type a value"
            value={value}
            onChange={(e) =>
              setFilterValues((prev) => ({
                ...prev,
                [filter.key]: e.target.value,
              }))
            }
            variant="outlined"
          />
        );
      case "number": {
        const nv = value || {
          operator: "equals",
          number: "",
          fromNumber: "",
          toNumber: "",
        };
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: nv?.operator !== "between" ? "row" : "column",
              gap: 1,
              alignItems: nv?.operator !== "between" ? "center" : "stretch",
            }}
          >
            <Select
              size="small"
              value={nv?.operator || "equals"}
              onChange={(e) =>
                setFilterValues((prev) => ({
                  ...prev,
                  [filter.key]: {
                    operator: e.target.value,
                    number: nv?.number || "",
                    fromNumber: "",
                    toNumber: "",
                  },
                }))
              }
              variant="outlined"
              sx={{
                minWidth: 90,
                width: nv?.operator === "between" ? "100%" : "auto",
                height: 40,
              }}
            >
              <MenuItem value="equals">=</MenuItem>
              <MenuItem value="notEquals">≠</MenuItem>
              <MenuItem value="greaterEquals">≥</MenuItem>
              <MenuItem value="lessEquals">≤</MenuItem>
              {filter?.showBetweenOperator && (
                <MenuItem value="between">Between</MenuItem>
              )}
            </Select>
            {nv?.operator !== "between" ? (
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="Enter value"
                value={nv?.number || ""}
                onChange={(e) =>
                  setFilterValues((prev) => ({
                    ...prev,
                    [filter.key]: { ...nv, number: e.target.value },
                  }))
                }
                variant="outlined"
              />
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <TextField
                  size="small"
                  type="number"
                  placeholder="From"
                  value={nv?.fromNumber || ""}
                  onChange={(e) =>
                    setFilterValues((prev) => ({
                      ...prev,
                      [filter.key]: { ...nv, fromNumber: e.target.value },
                    }))
                  }
                  variant="outlined"
                />
                <TextField
                  size="small"
                  type="number"
                  placeholder="To"
                  value={nv?.toNumber || ""}
                  onChange={(e) =>
                    setFilterValues((prev) => ({
                      ...prev,
                      [filter.key]: { ...nv, toNumber: e.target.value },
                    }))
                  }
                  variant="outlined"
                />
              </Box>
            )}
          </Box>
        );
      }
      case "date": {
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: value?.operator !== "between" ? "row" : "column",
              gap: 1,
            }}
          >
            <Select
              fullWidth
              size="small"
              value={value?.operator || "equals"}
              onChange={(e) =>
                setFilterValues((prev) => ({
                  ...prev,
                  [filter.key]: {
                    operator: e.target.value,
                    date: value?.date || "",
                    fromDate: "",
                    toDate: "",
                  },
                }))
              }
              variant="outlined"
              sx={{ width: value?.operator === "between" ? "none" : 100 }}
            >
              <MenuItem value="equals">=</MenuItem>
              <MenuItem value="greater">≥</MenuItem>
              <MenuItem value="less">≤</MenuItem>
              {filter?.showBetweenOperator && (
                <MenuItem value="between">Between</MenuItem>
              )}
            </Select>
            {value?.operator !== "between" && (
              <TextField
                fullWidth
                size="small"
                type="date"
                value={
                  value?.date ? value.date.split("-").reverse().join("-") : ""
                }
                onChange={(e) =>
                  setFilterValues((prev) => ({
                    ...prev,
                    [filter.key]: {
                      ...value,
                      date: e.target.value.split("-").reverse().join("-"),
                    },
                  }))
                }
                variant="outlined"
              />
            )}
            {value?.operator === "between" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  value={
                    value?.fromDate
                      ? value.fromDate.split("-").reverse().join("-")
                      : ""
                  }
                  onChange={(e) =>
                    setFilterValues((prev) => ({
                      ...prev,
                      [filter.key]: {
                        ...value,
                        fromDate: e.target.value.split("-").reverse().join("-"),
                      },
                    }))
                  }
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  value={
                    value?.toDate
                      ? value.toDate.split("-").reverse().join("-")
                      : ""
                  }
                  onChange={(e) =>
                    setFilterValues((prev) => ({
                      ...prev,
                      [filter.key]: {
                        ...value,
                        toDate: e.target.value.split("-").reverse().join("-"),
                      },
                    }))
                  }
                  variant="outlined"
                />
              </Box>
            )}
          </Box>
        );
      }
      case "time": {
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: value?.operator !== "between" ? "row" : "column",
              gap: 1,
            }}
          >
            <Select
              fullWidth
              size="small"
              value={value?.operator || "equals"}
              onChange={(e) =>
                setFilterValues((prev) => ({
                  ...prev,
                  [filter.key]: {
                    operator: e.target.value,
                    time: value?.time || "",
                    fromTime: value?.fromTime || "",
                    toTime: value?.toTime || "",
                  },
                }))
              }
              variant="outlined"
              sx={{
                width: value?.operator === "between" ? "none" : 80,
                height: 40,
                mt: 1,
              }}
            >
              <MenuItem value="equals">=</MenuItem>
              <MenuItem value="greater">≥</MenuItem>
              <MenuItem value="less">≤</MenuItem>
              {filter?.showBetweenOperator && (
                <MenuItem value="between">Between</MenuItem>
              )}
            </Select>
            {value?.operator !== "between" && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={["TimePicker"]}>
                  <div style={{ width: 150 }}>
                    <TimePicker
                      ampm={false}
                      viewRenderers={{
                        hours: renderTimeViewClock,
                        minutes: renderTimeViewClock,
                        seconds: renderTimeViewClock,
                      }}
                      value={value?.time ? dayjs(value.time, "HH:mm") : null}
                      onChange={(nv) =>
                        setFilterValues((prev) => ({
                          ...prev,
                          [filter.key]: {
                            ...value,
                            time: nv ? dayjs(nv).format("HH:mm") : "",
                          },
                        }))
                      }
                      slotProps={{
                        textField: { size: "small", variant: "outlined" },
                      }}
                    />
                  </div>
                </DemoContainer>
              </LocalizationProvider>
            )}
            {value?.operator === "between" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={["TimePicker"]}>
                    <TimePicker
                      ampm={false}
                      viewRenderers={{
                        hours: renderTimeViewClock,
                        minutes: renderTimeViewClock,
                        seconds: renderTimeViewClock,
                      }}
                      value={
                        value?.fromTime ? dayjs(value.fromTime, "HH:mm") : null
                      }
                      onChange={(nv) =>
                        setFilterValues((prev) => ({
                          ...prev,
                          [filter.key]: {
                            ...value,
                            fromTime: nv ? dayjs(nv).format("HH:mm") : "",
                          },
                        }))
                      }
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          variant: "outlined",
                        },
                      }}
                    />
                  </DemoContainer>
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={["TimePicker"]}>
                    <TimePicker
                      ampm={false}
                      viewRenderers={{
                        hours: renderTimeViewClock,
                        minutes: renderTimeViewClock,
                        seconds: renderTimeViewClock,
                      }}
                      value={
                        value?.toTime ? dayjs(value.toTime, "HH:mm") : null
                      }
                      onChange={(nv) =>
                        setFilterValues((prev) => ({
                          ...prev,
                          [filter.key]: {
                            ...value,
                            toTime: nv ? dayjs(nv).format("HH:mm") : "",
                          },
                        }))
                      }
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          variant: "outlined",
                        },
                      }}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </Box>
            )}
          </Box>
        );
      }
      case "autocomplete": {
        const dynamicOptions = filter.options || getDynamicOptions(filter.key);
        const options = ["All", ...dynamicOptions];
        return (
          <Autocomplete
            multiple
            fullWidth
            size="small"
            options={options}
            disableCloseOnSelect
            value={getCurrentValue(value)}
            onChange={(e, nv) => handleMultipleSelection(nv, filter.key)}
            getOptionLabel={(o) => o}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  checked={selected}
                  sx={{
                    color: option === "primary.main",
                    "&.Mui-checked": { color: option === "primary.main" },
                  }}
                />
                <Typography sx={{ color: option === "primary.main" }}>
                  {option}
                </Typography>
              </li>
            )}
            filterOptions={(opts, { inputValue }) =>
              opts.filter((o) =>
                o.toString().toLowerCase().includes(inputValue.toLowerCase()),
              )
            }
            ListboxProps={{ style: { maxHeight: 200 } }}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option} size="small" {...getTagProps({ index })} />
              ))
            }
          />
        );
      }
      case "boolean":
        return (
          <Select
            fullWidth
            size="small"
            value={value || ""}
            onChange={(e) =>
              setFilterValues((prev) => ({
                ...prev,
                [filter.key]: e.target.value,
              }))
            }
            variant="outlined"
            displayEmpty
          >
            <MenuItem value="">{filter.labels?.all || "All"}</MenuItem>
            <MenuItem value="true">{filter.labels?.true || "Yes"}</MenuItem>
            <MenuItem value="false">{filter.labels?.false || "No"}</MenuItem>
          </Select>
        );
      default:
        return null;
    }
  };

  // ── Chip display helpers ──────────────────────────────────────────────────
  const formatDate = (d) => d;
  const getChipDisplayValue = (value, filter) => {
    if (filter?.Viewmode)
      return Array.isArray(value) && value.length > 0
        ? `: ${value[0]}`
        : `: ${value ?? ""}`;
    if (Array.isArray(value) && filter?.type === "autocomplete") {
      if (value.length === 0 || value.includes("All"))
        return filter.labels?.all || "All";
      if (value.length === 1) return `: ${value[0]}`;
      if (value.length === 2) return `: ${value.join(", ")}`;
      return `: ${value.length} selected`;
    }
    if (filter.type === "boolean") {
      if (!value || value === "") return filter.labels?.all || "All";
      return value === "true"
        ? filter.labels?.true || "Yes"
        : filter.labels?.false || "No";
    }
    if (filter.type === "date" && typeof value === "object") {
      const op =
        { equals: "=", greater: "≥", less: "≤", between: "Between" }[
          value.operator
        ] || "=";
      if (value.operator !== "between" && value.date)
        return `: ${op} ${formatDate(value.date)}`;
      if (value.operator === "between") {
        if (value.fromDate && value.toDate)
          return `: ${op} ${formatDate(value.fromDate)} - ${formatDate(value.toDate)}`;
        if (value.fromDate) return `: ${op} From ${formatDate(value.fromDate)}`;
        if (value.toDate) return `: ${op} To ${formatDate(value.toDate)}`;
      }
      return filter.labels?.all || "All";
    }
    if (filter.type === "time" && typeof value === "object") {
      const op =
        { equals: "=", greater: "≥", less: "≤", between: "Between" }[
          value.operator
        ] || "=";
      if (value.operator !== "between" && value.time)
        return `: ${op} ${value.time}`;
      if (value.operator === "between") {
        if (value.fromTime && value.toTime)
          return `: ${op} ${value.fromTime} - ${value.toTime}`;
        if (value.fromTime) return `: ${op} From ${value.fromTime}`;
        if (value.toTime) return `: ${op} To ${value.toTime}`;
      }
      return "";
    }
    if (filter.type === "number") {
      if (typeof value === "object" && value !== null) {
        const op =
          {
            equals: "=",
            notEquals: "≠",
            greaterEquals: "≥",
            lessEquals: "≤",
            between: "Between",
          }[value.operator] || "=";
        if (
          value.operator !== "between" &&
          value.number !== undefined &&
          value.number !== ""
        )
          return `: ${op} ${value.number}`;
        if (value.operator === "between") {
          if (
            value.fromNumber !== undefined &&
            value.fromNumber !== "" &&
            value.toNumber !== undefined &&
            value.toNumber !== ""
          )
            return `: ${op} ${value.fromNumber} - ${value.toNumber}`;
          if (value.fromNumber !== undefined && value.fromNumber !== "")
            return `: ${op} From ${value.fromNumber}`;
          if (value.toNumber !== undefined && value.toNumber !== "")
            return `: ${op} To ${value.toNumber}`;
        }
        return "";
      }
      return value !== undefined && value !== "" && value !== null
        ? `: ${value}`
        : "";
    }
    if (
      value === undefined ||
      value === null ||
      value === "" ||
      (typeof value === "object" &&
        !value?.date &&
        !value?.fromDate &&
        !value?.toDate)
    ) {
      if (filter.type === "autocomplete" || filter.type === "boolean")
        return filter.labels?.all || "All";
      return "";
    }
    return `: ${value}`;
  };

  const inputRef = useRef(null);
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const hasActiveFiltersOrSort =
    chipFilters.length > 0 || sortRules?.length > 0 ||
    Boolean(!pickerDateField && serverTable?.state[serverConfig?.defaultDateField || "event_time"] === "all");
  const chipRowHasContent =
    hasActiveFiltersOrSort ||
    (showSavedViewControl && showSavedViewChipsInFilterRow);
  const effectiveFilteredData = filteredDataReady ? filteredData : data;

  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const [selectedDownloadType, setSelectedDownloadType] =
    useState("summarized");
  const [isDetailedDownloadInProgress, setIsDetailedDownloadInProgress] =
    useState(false);
  const [detailedDownloadProgress, setDetailedDownloadProgress] = useState(0);
  const [isProgressDeterminate, setIsProgressDeterminate] = useState(true);
  const openDownloadMenu = Boolean(downloadAnchorEl);
  const setPollingBlocked = serverTable?.setDrawerOpen;
  useEffect(() => {
    setPollingBlocked?.(Boolean(anchorEl || anchor || (sortOpen && sortRules.length > 0) || addSortMenuAnchor || downloadAnchorEl));
  }, [setPollingBlocked, anchorEl, anchor, sortOpen, sortRules.length, addSortMenuAnchor, downloadAnchorEl]);
  const hasFormatOptions = !showDownloadOptions && downloadFormats.length > 1;
  const baseExportRowCount =
    (Array.isArray(effectiveFilteredData) && effectiveFilteredData.length > 0
      ? effectiveFilteredData
      : data
    )?.length || 0;
  // A screen with a Summarized/Detailed type selector can pass a single
  // pdfRowCountOverride/pdfColumnCountOverride sized for the (much larger)
  // Detailed export — applying that same number while "Summarized" is
  // selected would wrongly disable a PDF that's actually well within limits.
  // Only apply the override for the export it was actually measured for:
  // the Detailed one, or any screen that doesn't offer the type selector at
  // all (its single override always describes its one download).
  const overrideAppliesToCurrentType =
    !showDownloadTypeSelector || selectedDownloadType === "detailed";
  // Number.isFinite (not the coercing Number(x) then isFinite) so a
  // caller that leaves the override at its default `null` is correctly
  // treated as "no override" — Number(null) is 0, which is itself finite,
  // so the old `Number.isFinite(Number(override))` check silently forced
  // exportRowCount/exportColumnCount to 0 (always under the limit) for
  // every screen that didn't explicitly pass one of these props.
  const exportRowCount =
    overrideAppliesToCurrentType && Number.isFinite(pdfRowCountOverride)
      ? Number(pdfRowCountOverride)
      : baseExportRowCount;
  const isPdfTooLarge = exportRowCount > pdfMaxRows;
  const menuExportHeaders =
    getDetailedHeaders && overrideAppliesToCurrentType
      ? getDetailedHeaders()
      : filterHeaders;
  // +1 accounts for the auto-added "S No" column every export gets.
  const exportColumnCount =
    overrideAppliesToCurrentType && Number.isFinite(pdfColumnCountOverride)
      ? Number(pdfColumnCountOverride)
      : getFilteredHeaders(menuExportHeaders).length + 1;
  const isPdfTooWide = exportColumnCount > MAX_PDF_COLUMNS;
  const hasNoExportData =
    !Array.isArray(data) ||
    data.length === 0 ||
    !Array.isArray(effectiveFilteredData) ||
    effectiveFilteredData.length === 0;
  const summarizedDownloadState = downloadOptionStates?.summarized || {};
  const detailedDownloadState = downloadOptionStates?.detailed || {};
  const isSummarizedDownloadDisabled = Boolean(summarizedDownloadState.disabled);
  const isDetailedDownloadOptionDisabled = Boolean(
    detailedDownloadState.disabled,
  );
  const areAllTypedDownloadsDisabled =
    showDownloadOptions &&
    showDownloadTypeSelector &&
    isSummarizedDownloadDisabled &&
    isDetailedDownloadOptionDisabled;
  const isDownloadDisabled = hasNoExportData || areAllTypedDownloadsDisabled;
  const resolvedDownloadDisabledReason =
    downloadDisabledReason ||
    summarizedDownloadState.reason ||
    detailedDownloadState.reason ||
    "No data available to export";
  const exportTooltipTitle = isDetailedDownloadInProgress
    ? "Downloading..."
    : isDownloadDisabled
      ? resolvedDownloadDisabledReason
      : "Export";
  const customExportTooltipTitle = isCustomDownloadInProgress
    ? "Downloading..."
    : isDownloadDisabled
      ? resolvedDownloadDisabledReason
      : "Export";

  useEffect(() => {
    const syncDownloadProgress = () => {
      try {
        const rawState = sessionStorage.getItem(DETAILED_DOWNLOAD_PROGRESS_KEY);
        if (!rawState) {
          setIsDetailedDownloadInProgress(false);
          setDetailedDownloadProgress(0);
          setIsProgressDeterminate(true);
          return;
        }

        const parsedState = JSON.parse(rawState);
        setIsDetailedDownloadInProgress(Boolean(parsedState?.active));
        setDetailedDownloadProgress(Number(parsedState?.progress) || 0);
        setIsProgressDeterminate(parsedState?.determinate !== false);
      } catch {
        setIsDetailedDownloadInProgress(false);
        setDetailedDownloadProgress(0);
        setIsProgressDeterminate(true);
      }
    };

    syncDownloadProgress();
    window.addEventListener(
      "detailed-download-progress",
      syncDownloadProgress,
    );

    return () => {
      window.removeEventListener(
        "detailed-download-progress",
        syncDownloadProgress,
      );
    };
  }, []);

  const updateDetailedDownloadProgressState = (
    active,
    progress,
    determinate = true,
  ) => {
    const safeProgress = Number(progress) || 0;
    const payload = { active, progress: safeProgress, determinate };

    if (active) {
      sessionStorage.setItem(
        DETAILED_DOWNLOAD_PROGRESS_KEY,
        JSON.stringify(payload),
      );
    } else {
      sessionStorage.removeItem(DETAILED_DOWNLOAD_PROGRESS_KEY);
    }

    setIsDetailedDownloadInProgress(active);
    setDetailedDownloadProgress(safeProgress);
    setIsProgressDeterminate(determinate);
    window.dispatchEvent(new Event("detailed-download-progress"));
  };

  const handleDownloadClick = (event) => {
    if (showDownloadOptions && showDownloadTypeSelector) {
      if (isSummarizedDownloadDisabled && !isDetailedDownloadOptionDisabled) {
        setSelectedDownloadType("detailed");
      } else if (
        isDetailedDownloadOptionDisabled &&
        !isSummarizedDownloadDisabled
      ) {
        setSelectedDownloadType("summarized");
      }
    }
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  const handleExcelDownloadWithOptions = async (downloadType) => {
    handleDownloadClose();

    if (!screenAccess) {
      showToast("error", "Access denied");
      return;
    }

    const exportBaseData =
      Array.isArray(effectiveFilteredData) && effectiveFilteredData.length > 0
        ? effectiveFilteredData
        : data;

    if (downloadType === "summarized") {
      let dataToDownload = exportBaseData;

      updateDetailedDownloadProgressState(true, 0);
      if (onRequestSummarizedData) {
        try {
          const result = await onRequestSummarizedData(exportBaseData);
          if (result?.data) dataToDownload = result.data;
        } finally {
          updateDetailedDownloadProgressState(true, 25);
        }
      }

      try {
        if (customSummarizedDownloadHandler) {
          await customSummarizedDownloadHandler(
            dataToDownload,
            includeMetadata ? withFreshGeneratedOn(metadata) : null,
            filterValues,
            sortRules,
          );
          showToast("success", "Summarized report downloaded");
          return;
        }

        await downloadExcel(
          dataToDownload,
          filterHeaders,
          "Summarized Report",
          filterValues,
          includeMetadata ? withFreshGeneratedOn(metadata) : null,
          sortRules,
          (progressValue) => {
            updateDetailedDownloadProgressState(true, progressValue);
          },
        );
        showToast("success", "Summarized report downloaded");
      } finally {
        updateDetailedDownloadProgressState(false, 0);
      }
    } else if (downloadType === "detailed") {
      if (onRequestDetailedData) {
        updateDetailedDownloadProgressState(true, 0);
        try {
          const detailedData = await onRequestDetailedData(
            exportBaseData,
            (progressValue) => {
              updateDetailedDownloadProgressState(true, progressValue);
            },
          );
          if (detailedData?.status === "preparing") {
            return true;
          }
          if (!detailedData || detailedData.length === 0) {
            showToast("info", "No detailed records are available for export.");
            return true;
          }
          const detailedHeaders = getDetailedHeaders
            ? getDetailedHeaders()
            : filterHeaders;

          if (customDetailedDownloadHandler) {
            customDetailedDownloadHandler(
              detailedData,
              includeMetadata ? withFreshGeneratedOn(metadata) : null,
              filterValues,
              sortRules,
            );
            showToast("success", "Detailed report downloaded");
            return true;
          }

          downloadExcel(
            detailedData,
            detailedHeaders,
            "Detailed Report",
            filterValues,
            includeMetadata ? withFreshGeneratedOn(metadata) : null,
            sortRules,
          );
          showToast("success", "Detailed report downloaded");
        } finally {
          updateDetailedDownloadProgressState(false, 0);
        }
      } else {
        showToast("error", "Detailed data not available");
      }
    }
  };

  const handleFormatDownloadWithType = async (downloadType, downloadFormat) => {
    handleDownloadClose();

    if (!screenAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to download report",
      );
      return;
    }

    const exportBaseData =
      Array.isArray(effectiveFilteredData) && effectiveFilteredData.length > 0
        ? effectiveFilteredData
        : data;

    if (downloadType === "summarized") {
      let dataToDownload = exportBaseData;

      updateDetailedDownloadProgressState(true, 0);
      if (onRequestSummarizedData) {
        try {
          const result = await onRequestSummarizedData(exportBaseData);
          if (result?.data) dataToDownload = result.data;
        } finally {
          updateDetailedDownloadProgressState(true, 25);
        }
      }

      try {
        if (customSummarizedDownloadHandler) {
          await customSummarizedDownloadHandler(
            dataToDownload,
            includeMetadata ? withFreshGeneratedOn(metadata) : null,
            filterValues,
            sortRules,
            downloadFormat,
            (progressValue) => {
              updateDetailedDownloadProgressState(true, progressValue);
            },
          );
          return;
        }

        const summarizedHeaders = filterHeaders;
        if (downloadFormat === "pdf") {
          await downloadPdf(
            dataToDownload,
            summarizedHeaders,
            "Summarized Report",
            filterValues,
            includeMetadata ? withFreshGeneratedOn(metadata) : null,
            sortRules,
            (progressValue) => {
              updateDetailedDownloadProgressState(true, progressValue);
            },
          );
        } else if (downloadFormat === "csv") {
          await downloadCsv(
            dataToDownload,
            summarizedHeaders,
            "Summarized Report",
            filterValues,
            includeMetadata ? withFreshGeneratedOn(metadata) : null,
            sortRules,
            (progressValue) => {
              updateDetailedDownloadProgressState(true, progressValue);
            },
          );
        } else {
          await downloadExcel(
            dataToDownload,
            summarizedHeaders,
            "Summarized Report",
            filterValues,
            includeMetadata ? withFreshGeneratedOn(metadata) : null,
            sortRules,
            (progressValue) => {
              updateDetailedDownloadProgressState(true, progressValue);
            },
          );
        }
      } finally {
        updateDetailedDownloadProgressState(false, 0);
      }
      return;
    }

    if (isDetailedDownloadInProgress) return;

    const detailedHeaders = getDetailedHeaders
      ? getDetailedHeaders()
      : filterHeaders;

    if (customDetailedDownloadHandler) {
      updateDetailedDownloadProgressState(true, 0);
      try {
        await customDetailedDownloadHandler(
          exportBaseData,
          includeMetadata ? withFreshGeneratedOn(metadata) : null,
          filterValues,
          sortRules,
          downloadFormat,
          (progressValue) => {
            updateDetailedDownloadProgressState(true, progressValue);
          },
        );
      } finally {
        updateDetailedDownloadProgressState(false, 0);
      }
      return;
    }

    if (onRequestDetailedData) {
      updateDetailedDownloadProgressState(true, 0);
      try {
        const detailedData = await onRequestDetailedData(
          exportBaseData,
          (progressValue) => {
            updateDetailedDownloadProgressState(true, progressValue);
          },
        );
        if (!detailedData || detailedData.length === 0) {
          showToast("info", "No detailed records are available for export.");
          return;
        }

        if (downloadFormat === "pdf") {
          await downloadPdf(
            detailedData,
            detailedHeaders,
            "Detailed Report",
            filterValues,
            includeMetadata ? withFreshGeneratedOn(metadata) : null,
            sortRules,
          );
        } else if (downloadFormat === "csv") {
          await downloadCsv(
            detailedData,
            detailedHeaders,
            "Detailed Report",
            filterValues,
            includeMetadata ? withFreshGeneratedOn(metadata) : null,
            sortRules,
          );
        } else {
          await downloadExcel(
            detailedData,
            detailedHeaders,
            "Detailed Report",
            filterValues,
            includeMetadata ? withFreshGeneratedOn(metadata) : null,
            sortRules,
          );
        }
      } finally {
        updateDetailedDownloadProgressState(false, 0);
      }
    }
  };

  return (
    <>
      {/* ── Top toolbar ───────────────────────────────────────────────────── */}
      <Box
        sx={{
          p: { xs: 0.75, sm: 1 },
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: { xs: 1, sm: 2 },
          flexWrap: { xs: "wrap", md: "nowrap" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flex: { xs: "1 1 100%", sm: 1 },
            minWidth: 0,
          }}
        >
          {showFilter ? (
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {filters ?? null}
            </Typography>
          ) : titleOverride ? (
            titleOverride
          ) : showtitle_count && !hideCount ? (
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {`${effectiveFilteredData?.length ?? 0} ${title}`}
            </Typography>
          ) : null}
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            ml: { xs: 0, sm: "auto" },
            alignItems: "center",
            justifyContent: { xs: "flex-start", sm: "flex-end" },
            flexWrap: { xs: "wrap", md: "nowrap" },
            width: { xs: "100%", sm: "auto" },
            minWidth: 0,
          }}
        >
          {toolbarActions}
          {/* Search */}
          {labeledToolbar ? (
            <Box
              sx={filterToolbarSearchSx}
            >
              <Search sx={{ fontSize: 19, color: designColors.slate400 }} />
              <InputBase
                inputRef={inputRef}
                placeholder={searchPlaceholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => serverTable?.setSearchFocused(true)}
                onBlur={() => serverTable?.setSearchFocused(false)}
                inputProps={serverTable ? { maxLength: 100, "aria-label": "Search events" } : undefined}
                sx={{
                  flex: 1,
                  fontSize: 13.5,
                  color: designColors.slate800,
                }}
              />
            </Box>
          ) : (
            <Card
              variant="outlined"
              sx={{
                display: "flex",
                alignItems: "center",
                borderRadius: open ? 2 : 50,
                overflow: "hidden",
                bgcolor: !open && themes?.primary,
                p: 0,
                width: "fit-content",
                transition: "border-radius 0.3s ease",
              }}
            >
              <Tooltip title="Search" arrow slotProps={tooltipStyleProps}>
                <IconButton
                  onClick={() => setOpen((prev) => !prev)}
                  sx={{
                    borderRadius: open ? 0 : 50,
                    borderRight: open ? "1px solid" : "none",
                    borderColor: "divider",
                    transition: "border-radius 0.3s ease",
                  }}
                >
                  <Search />
                </IconButton>
              </Tooltip>
              <Box
                sx={{
                  height: 40,
                  width: open ? 200 : 0,
                  transition: "width 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <InputBase
                  inputRef={inputRef}
                  placeholder="Search Sessions"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  sx={{
                    flex: 1,
                    opacity: open ? 1 : 0,
                    transition: "opacity 0.2s ease",
                    px: 2,
                  }}
                />
              </Box>
            </Card>
          )}

          {/* Reprocess */}
          {showReprocessButton && (
            <Tooltip title="Reprocess" arrow slotProps={tooltipStyleProps}>
              <IconButton
                onClick={() => setShowReprocessDialog(true)}
                sx={{ ...iconButtonStyle, color: themes?.blackColor }}
                disabled={hasNoExportData}
              >
                <ReplayIcon />
              </IconButton>
            </Tooltip>
          )}

          {showDownloadOptions || hasFormatOptions ? (
            <>
              {labeledToolbar ? (
                <Tooltip
                  title={exportTooltipTitle}
                  arrow
                  slotProps={tooltipStyleProps}
                >
                  <span>
                    <Button
                      variant="outlined"
                      onClick={handleDownloadClick}
                      disabled={isDownloadDisabled || isDetailedDownloadInProgress}
                      startIcon={
                        isDetailedDownloadInProgress ? (
                          <CircularProgress size={16} thickness={5} />
                        ) : (
                          <FileDownload sx={{ fontSize: 18 }} />
                        )
                      }
                      sx={labeledButtonSx}
                    >
                      {isDetailedDownloadInProgress
                        ? isProgressDeterminate
                          ? `Downloading ${Math.round(detailedDownloadProgress)}%`
                          : "Downloading…"
                        : "Download"}
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip
                  title={exportTooltipTitle}
                  arrow
                  slotProps={tooltipStyleProps}
                >
                  <span>
                    <IconButton
                      onClick={handleDownloadClick}
                      sx={{
                        ...iconButtonStyle,
                        color: themes?.blackColor,
                        position: "relative",
                        width: 40,
                        height: 40,
                        borderColor: isDetailedDownloadInProgress
                          ? "transparent"
                          : iconButtonStyle.borderColor,
                      }}
                      disabled={isDownloadDisabled || isDetailedDownloadInProgress}
                    >
                      {isDetailedDownloadInProgress ? (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative",
                          }}
                        >
                          {isProgressDeterminate ? (
                            <>
                              <CircularProgress
                                variant="determinate"
                                value={100}
                                size={44}
                                thickness={3}
                                sx={{
                                  position: "absolute",
                                  color: "#d9e7f7",
                                }}
                              />
                              <CircularProgress
                                variant="determinate"
                                value={Math.max(detailedDownloadProgress, 0)}
                                size={44}
                                thickness={3}
                                sx={{
                                  position: "absolute",
                                  color: "#1976d2",
                                }}
                              />
                              <Typography
                                sx={{
                                  position: "absolute",
                                  top: "50%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                  fontSize: "12px",
                                  fontWeight: 800,
                                  lineHeight: 1,
                                  color: "#1976d2",
                                  textAlign: "center",
                                  width: "100%",
                                  whiteSpace: "nowrap",
                                  pointerEvents: "none",
                                }}
                              >
                                {Math.round(detailedDownloadProgress)}
                              </Typography>
                            </>
                          ) : (
                            <CircularProgress
                              variant="indeterminate"
                              size={24}
                              thickness={4}
                              sx={{ color: "#1976d2" }}
                            />
                          )}
                        </Box>
                      ) : (
                        <FileDownload />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {/* Download Options Menu */}
              <Popover
                open={openDownloadMenu}
                anchorEl={downloadAnchorEl}
                onClose={handleDownloadClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    width: 220,
                    borderRadius: 2,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                  },
                }}
              >
                <List dense sx={{ p: 0 }}>
                  {showDownloadOptions && showDownloadTypeSelector ? (
                    <Box sx={{ p: 1.5, width: 280 }}>
                      <Typography
                        sx={{ fontSize: 13, fontWeight: 700, mb: 1, color: "text.secondary" }}
                      >
                        Download Type
                      </Typography>
                      <List dense sx={{ p: 0, mb: 1 }}>
                        {[
                          { id: "summarized", label: "Summarized Report" },
                          { id: "detailed", label: "Detailed Report" },
                        ].map((option) => {
                          const optionState =
                            option.id === "summarized"
                              ? summarizedDownloadState
                              : detailedDownloadState;
                          const optionDisabled =
                            Boolean(optionState?.disabled) ||
                            (option.id === "detailed" &&
                              isDetailedDownloadInProgress);

                          return (
                            <Tooltip
                              key={option.id}
                              title={optionDisabled ? optionState?.reason || "" : ""}
                              arrow
                              slotProps={tooltipStyleProps}
                            >
                              <span>
                                <ListItemButton
                                  onClick={() => setSelectedDownloadType(option.id)}
                                  disabled={optionDisabled}
                                  sx={{ borderRadius: 1, mb: 0.5 }}
                                >
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    {selectedDownloadType === option.id ? (
                                      <RadioButtonChecked fontSize="small" />
                                    ) : (
                                      <RadioButtonUnchecked fontSize="small" />
                                    )}
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={
                                      option.id === "detailed" &&
                                      isDetailedDownloadInProgress
                                        ? `Detailed Report (${Math.round(detailedDownloadProgress)}%)`
                                        : option.label
                                    }
                                    primaryTypographyProps={{
                                      fontWeight: 500,
                                      fontSize: 14,
                                    }}
                                  />
                                </ListItemButton>
                              </span>
                            </Tooltip>
                          );
                        })}
                      </List>

                      <Divider sx={{ mb: 1.25 }} />

                      <Typography
                        sx={{ fontSize: 13, fontWeight: 700, mb: 1, color: "text.secondary" }}
                      >
                        Format
                      </Typography>
                      <List dense sx={{ p: 0 }}>
                        {downloadFormats.map((format, index) => {
                          const disablePdf =
                            format === "pdf" && (isPdfTooLarge || isPdfTooWide);
                          const pdfDisabledReason = isPdfTooLarge
                            ? `PDF supports up to ${pdfMaxRows} rows. This report has ${exportRowCount} rows — use Excel instead.`
                            : isPdfTooWide
                              ? `PDF supports up to ${MAX_PDF_COLUMNS} columns. This report has ${exportColumnCount} columns — use Excel instead.`
                              : "";
                          return (
                            <React.Fragment key={format}>
                              {index > 0 ? <Divider /> : null}
                              <Tooltip
                                title={
                                  disablePdf
                                    ? pdfDisabledReason
                                    : selectedDownloadType === "summarized" &&
                                        summarizedDownloadState?.disabled
                                      ? summarizedDownloadState?.reason || ""
                                      : selectedDownloadType === "detailed" &&
                                          detailedDownloadState?.disabled
                                        ? detailedDownloadState?.reason || ""
                                        : ""
                                }
                                arrow
                                slotProps={tooltipStyleProps}
                              >
                                <span>
                                  <ListItemButton
                                    onClick={() =>
                                      handleFormatDownloadWithType(
                                        selectedDownloadType,
                                        format,
                                      )
                                    }
                                    sx={{ py: 1.5 }}
                                    disabled={
                                      disablePdf ||
                                      (selectedDownloadType === "summarized" &&
                                        isSummarizedDownloadDisabled) ||
                                      (selectedDownloadType === "detailed" &&
                                        isDetailedDownloadOptionDisabled)
                                    }
                                  >
                                    <ListItemText
                                      primary={format.toUpperCase()}
                                      primaryTypographyProps={{
                                        fontWeight: 500,
                                        fontSize: 14,
                                      }}
                                    />
                                  </ListItemButton>
                                </span>
                              </Tooltip>
                            </React.Fragment>
                          );
                        })}
                      </List>
                    </Box>
                  ) : showDownloadOptions ? (
                    <>
                      <ListItemButton
                        onClick={() => handleExcelDownloadWithOptions("summarized")}
                        sx={{ py: 1.5 }}
                      >
                        <ListItemText
                          primary="Summarized Report"
                          primaryTypographyProps={{ fontWeight: 500, fontSize: 14 }}
                        />
                      </ListItemButton>

                      <Divider />

                      <ListItemButton
                        onClick={() => handleExcelDownloadWithOptions("detailed")}
                        sx={{ py: 1.5 }}
                        disabled={isDetailedDownloadInProgress}
                      >
                        <ListItemText
                          primary={
                            isDetailedDownloadInProgress
                              ? `Downloading... ${Math.round(detailedDownloadProgress)}`
                              : "Detailed Report"
                          }
                          primaryTypographyProps={{ fontWeight: 500, fontSize: 14 }}
                        />
                      </ListItemButton>
                    </>
                  ) : (
                    downloadFormats.map((format, index) => {
                      const disablePdf =
                        format === "pdf" && (isPdfTooLarge || isPdfTooWide);
                      const pdfDisabledReason = isPdfTooLarge
                        ? `PDF supports up to ${pdfMaxRows} rows. This report has ${exportRowCount} rows — use Excel instead.`
                        : isPdfTooWide
                          ? `PDF supports up to ${MAX_PDF_COLUMNS} columns. This report has ${exportColumnCount} columns — use Excel instead.`
                          : "";
                      return (
                      <React.Fragment key={format}>
                        {index > 0 ? <Divider /> : null}
                        <Tooltip
                          title={disablePdf ? pdfDisabledReason : ""}
                          arrow
                          slotProps={tooltipStyleProps}
                        >
                          <span>
                            <ListItemButton
                              onClick={() => handleFormatDownload(format)}
                              sx={{ py: 1.5 }}
                              disabled={disablePdf}
                            >
                              <ListItemText
                                primary={format.toUpperCase()}
                                primaryTypographyProps={{ fontWeight: 500, fontSize: 14 }}
                              />
                            </ListItemButton>
                          </span>
                        </Tooltip>
                      </React.Fragment>
                      );
                    })
                  )}
                </List>
              </Popover>
            </>
          ) : labeledToolbar ? (
            <Tooltip
              title={customExportTooltipTitle}
              arrow
              slotProps={tooltipStyleProps}
            >
              <span>
                <Button
                  variant="outlined"
                  onClick={async () => {
                    if (screenAccess) handleExcelDownload();
                    else
                      showToast(
                        "error",
                        "Access denied: You are not authorized to download report",
                      );
                  }}
                  disabled={isCustomDownloadInProgress || isDownloadDisabled}
                  startIcon={
                    isCustomDownloadInProgress ? (
                      <CircularProgress size={16} thickness={5} />
                    ) : (
                      <FileDownload sx={{ fontSize: 18 }} />
                    )
                  }
                  sx={labeledButtonSx}
                >
                  {isCustomDownloadInProgress
                    ? `Downloading ${Math.round(customDownloadProgress)}%`
                    : "Download"}
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Tooltip
              title={customExportTooltipTitle}
              arrow
              slotProps={tooltipStyleProps}
            >
              <span>
                <IconButton
                  onClick={async () => {
                    if (screenAccess) handleExcelDownload();
                    else
                      showToast(
                        "error",
                        "Access denied: You are not authorized to download report",
                      );
                  }}
                  sx={{
                    ...iconButtonStyle,
                    color: themes?.blackColor,
                    position: "relative",
                    width: 40,
                    height: 40,
                    borderColor: isCustomDownloadInProgress
                      ? "transparent"
                      : iconButtonStyle.borderColor,
                  }}
                  disabled={isCustomDownloadInProgress || isDownloadDisabled}
                >
                  {isCustomDownloadInProgress ? (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                      }}
                    >
                      <CircularProgress
                        variant="determinate"
                        value={100}
                        size={44}
                        thickness={3}
                        sx={{
                          position: "absolute",
                          color: "#d9e7f7",
                        }}
                      />
                      <CircularProgress
                        variant="determinate"
                        value={Math.max(customDownloadProgress, 0)}
                        size={44}
                        thickness={3}
                        sx={{
                          position: "absolute",
                          color: "#1976d2",
                        }}
                      />
                      <Typography
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          fontSize: "12px",
                          fontWeight: 800,
                          lineHeight: 1,
                          color: "#1976d2",
                          textAlign: "center",
                          width: "100%",
                          whiteSpace: "nowrap",
                          pointerEvents: "none",
                        }}
                      >
                        {Math.round(customDownloadProgress)}
                      </Typography>
                    </Box>
                  ) : (
                    <FileDownload />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}

          {/* ── Sort button with badge ────────────────────────────────────── */}
          {showtitle_count && viewFilter && showSavedViewControl && (
            <Tooltip title="Save View" arrow slotProps={tooltipStyleProps}>
              <span>
                <IconButton
                  onClick={(event) => {
                    if (screenAccess) handleOpenSaveViewPopover(event);
                    else {
                      showToast(
                        "error",
                        "Access denied: You are not authorized to save views",
                      );
                    }
                  }}
                  disabled={savedViewHeaders.length === 0}
                  sx={iconButtonStyle}
                >
                  <ViewWeek />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {showtitle_count && viewFilter && (
            <Badge
              color="warning"
              overlap="circular"
              badgeContent={sortRules?.length || 0}
              invisible={!sortRules?.length}
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  minWidth: 18,
                  height: 18,
                },
              }}
            >
              <Tooltip title="Sort" arrow slotProps={tooltipStyleProps}>
                <span>
                  {labeledToolbar ? (
                    <Button
                      variant="outlined"
                      onClick={sortRules?.length ? openSortPanel : handleOpenAddSortMenu}
                      startIcon={<SwapVert sx={{ fontSize: 18 }} />}
                      sx={labeledButtonSx}
                    >
                      Sort
                    </Button>
                  ) : (
                    <IconButton
                      sx={iconButtonStyle}
                      onClick={sortRules?.length ? openSortPanel : handleOpenAddSortMenu}
                    >
                      <SwapVert />
                    </IconButton>
                  )}
                </span>
              </Tooltip>
            </Badge>
          )}

          {/* ── Filter button with badge ──────────────────────────────────── */}
          {showtitle_count && viewFilter && (
            <Badge
              color="warning"
              overlap="circular"
              badgeContent={chipFilters.length}
              invisible={!chipFilters.length}
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  minWidth: 18,
                  height: 18,
                },
              }}
            >
              <Tooltip title="Filter" arrow slotProps={tooltipStyleProps}>
                <span>
                  {labeledToolbar ? (
                    <Button
                      variant="outlined"
                      onClick={(event) => {
                        if (screenAccess) handleClick(event);
                        else
                          showToast(
                            "error",
                            "Access denied: You are not authorized to Add filter",
                          );
                      }}
                      disabled={!serverTable && selectedFilters?.length >= 1}
                      startIcon={<FilterAltOutlined sx={{ fontSize: 18 }} />}
                      endIcon={<ArrowDropDown />}
                      sx={labeledButtonSx}
                    >
                      Filters
                    </Button>
                  ) : (
                    <IconButton
                      onClick={(event) => {
                        if (screenAccess) handleClick(event);
                        else
                          showToast(
                            "error",
                            "Access denied: You are not authorized to Add filter",
                          );
                      }}
                      disabled={!serverTable && selectedFilters?.length >= 1}
                      sx={iconButtonStyle}
                    >
                      <FilterAltOutlined />
                    </IconButton>
                  )}
                </span>
              </Tooltip>
            </Badge>
          )}

          {/* ── Filter Popover ─────────────────────────────────────────────── */}
          <Popover
            open={openFilter}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            PaperProps={{ sx: { mt: 1, width: 220, borderRadius: 2 } }}
          >
            <Box sx={{ p: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Filter by..."
                value={filteredName}
                onChange={(e) => setFilteredName(e.target.value)}
                variant="outlined"
              />
            </Box>
            <List
              dense
              sx={{
                pt: 0,
                pb: 0.7,
                overflowY: "auto",
                height: addFilterHeaders?.length > 7 ? 200 : "auto",
              }}
            >
              {addFilterHeaders
                .filter(
                  (f) =>
                    f?.key &&
                    f?.name &&
                    f?.name
                      ?.toLowerCase()
                      ?.includes(filteredName?.toLowerCase() || "") &&
                    !selectedFilters.some((sf) => sf.key === f.key),
                )
                .map((f, i) => (
                  <ListItemButton
                    key={i}
                    onClick={() => {
                      setSelectedFilters((prev) => [...prev, f]);
                      setAnchorEl(null);
                      setValue("filter_clicked", true);
                      if (f.type === "date")
                        setFilterValues((prev) => ({
                          ...prev,
                          [f.key]: {
                            operator: "equals",
                            date: "",
                            fromDate: "",
                            toDate: "",
                          },
                        }));
                      if (f.type === "time")
                        setFilterValues((prev) => ({
                          ...prev,
                          [f.key]: {
                            operator: "equals",
                            time: "",
                            fromTime: "",
                            toTime: "",
                          },
                        }));
                      if (f.type === "autocomplete")
                        setFilterValues((prev) => ({
                          ...prev,
                          [f.key]: ["All"],
                        }));
                      setTimeout(() => {
                        const targetChip = chipRowRef.current?.querySelector(
                          `[data-filter-key="${f.key}"]`,
                        );
                        if (targetChip) {
                          setAnchor(targetChip);
                          setActiveFilter(f);
                        }
                      }, 100);
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {getHeaderOptionIcon(f.icon)}
                    </ListItemIcon>
                    <ListItemText
                      primary={f.name || "Unnamed"}
                      primaryTypographyProps={{ fontSize: 14 }}
                    />
                  </ListItemButton>
                ))}
            </List>
            {addFilterHeaders.filter(
              (f) =>
                f?.name &&
                f?.key &&
                f?.name
                  ?.toLowerCase()
                  ?.includes(filteredName?.toLowerCase() || "") &&
                !selectedFilters.some((sf) => sf.key === f.key),
            ).length === 0 && (
              <Typography
                variant="body2"
                sx={{ textAlign: "center", color: "#777" }}
              >
                No results
              </Typography>
            )}
          </Popover>

          <Popover
            open={openSaveViewPopover}
            anchorEl={saveViewAnchorEl}
            onClose={handleCloseSaveViewPopover}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: { mt: 1, width: 320, borderRadius: 2, p: 1.5 },
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Save Header View
              </Typography>
              <TextField
                size="small"
                placeholder="Enter view name"
                value={savedViewName}
                onChange={(event) => setSavedViewName(event.target.value)}
                fullWidth
              />
              <Box
                sx={{
                  maxHeight: 260,
                  overflowY: "auto",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 1,
                }}
              >
                {savedViewHeaders.map((header) => (
                  <Box
                    key={header.key}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      py: 0.25,
                    }}
                  >
                    <Checkbox
                      checked={selectedSavedViewHeaders.includes(header.key)}
                      onChange={() => toggleSavedViewHeader(header.key)}
                      size="small"
                    />
                    <Typography variant="body2">{header.name}</Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button size="small" onClick={handleCloseSaveViewPopover}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSaveHeaderView}
                >
                  Save
                </Button>
              </Box>
            </Box>
          </Popover>

          {newButtonComponent}
        </Box>
      </Box>

      {hideToolbarDivider ? null : <Divider />}

      {/* ── Chip row — ref'd so the sort popover can anchor here ─────────── */}
      <Box
        ref={chipRowRef}
        sx={{ p: chipRowHasContent ? 1 : 0, ml: chipRowHasContent ? 1 : 0 }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          {showChip && (
            <Box sx={{ display: "flex", gap: 1.2, flexWrap: "wrap", mt: chipRowHasContent ? 1 : 0 }}>
              {extraFilterChips}
              {/* ── Sort summary chip (visible when panel is closed & sorts exist) ── */}
              {sortRules?.length > 0 && (
                <Badge
                  color="warning"
                  variant="dot"
                  overlap="circular"
                  sx={{ "& .MuiBadge-badge": { top: 4, right: 6 } }}
                >
                  <Chip
                    icon={
                      <SwapVert
                        sx={{ fontSize: 14, ml: "6px", color: "inherit" }}
                      />
                    }
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Box component="span" sx={{ color: "warning.main" }}>
                          {sortRules.length}{" "}
                          {sortRules.length === 1 ? "sort" : "sorts"}
                        </Box>
                        <ArrowDropDown fontSize="small" />
                      </Box>
                    }
                    onClick={openSortPanel}
                    sx={{
                      borderRadius: "6px",
                      backgroundColor: "#e0e0e0",
                      color: "#000",
                      cursor: "pointer",
                    }}
                  />
                </Badge>
              )}

              {/* ── Filter chips ──────────────────────────────────────────────── */}
              {chipFilters.map((filter) => (
                <Badge
                  key={filter.key}
                  color="warning"
                  variant="dot"
                  overlap="circular"
                  sx={{ "& .MuiBadge-badge": { top: 4, right: 6 } }}
                >
                  <Chip
                    data-filter-key={filter.key}
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        {filter.name}
                        <Box component="span" sx={{ color: "warning.main" }}>
                          {getChipDisplayValue(
                            filterValues[filter.key],
                            filter,
                          )}
                        </Box>
                        <ArrowDropDown fontSize="small" />
                      </Box>
                    }
                    onClick={(e) => handleOpen(e, filter)}
                    sx={{
                      borderRadius: "6px",
                      backgroundColor: "#e0e0e0",
                      color: "#000",
                    }}
                  />
                </Badge>
              ))}

              {/* + filter chip */}
              {chipFilters.length > 0 && (
                <Chip
                  label="+ refine"
                  clickable
                  sx={{
                    color: "#000",
                    borderRadius: "6px",
                    height: 30,
                    backgroundColor: "#e0e0e0",
                  }}
                  onClick={(e) => {
                    if (screenAccess) setAnchorEl(e.currentTarget);
                    else
                      showToast(
                        "error",
                        "Access denied: You are not authorized to add filter",
                      );
                  }}
                />
              )}

              {showSavedViewControl && showSavedViewChipsInFilterRow && (
                <>
                  <Chip
                    label="All Headers"
                    clickable
                    color={activeSavedViewId ? "default" : "primary"}
                    variant={activeSavedViewId ? "outlined" : "filled"}
                    sx={{ borderRadius: "6px", height: 30 }}
                    onClick={() => onSelectSavedView?.(null)}
                  />

                  {savedViews.map((view) => (
                    <Chip
                      key={view.id}
                      label={view.name}
                      clickable
                      color={activeSavedViewId === view.id ? "primary" : "default"}
                      variant={activeSavedViewId === view.id ? "filled" : "outlined"}
                      onClick={() => onSelectSavedView?.(view.id)}
                      onDelete={(event) => {
                        event.stopPropagation();
                        onDeleteSavedView?.(view.id);
                      }}
                      deleteIcon={<Close fontSize="small" />}
                      sx={{ borderRadius: "6px", height: 30 }}
                    />
                  ))}
                </>
              )}

              {/* Individual filter popover */}
              <Popover
                open={openIndividualFilter}
                anchorEl={anchor}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                  sx: { p: 1.5, width: 260, borderRadius: 2, mt: 1 },
                }}
              >
                {activeFilter && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">
                        {activeFilter.name}
                      </Typography>
                      <IconButton size="small" aria-label="Filter actions" onClick={handleMenuOpen}>
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Box>
                    {renderFilterInput(activeFilter)}
                    {serverTable && <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1.5 }}>
                      <Button onClick={handleClose}>Cancel</Button>
                      <Button variant="outlined" color="warning" onClick={() => { if (updateFilterValues()) setAnchor(null); }}>Apply</Button>
                    </Box>}
                  </>
                )}
              </Popover>

              {/* Delete filter menu */}
              <Popover
                open={Boolean(menuAnchor)}
                anchorEl={menuAnchor}
                onClose={handleMenuClose}
                PaperProps={{ sx: { borderRadius: 1, minWidth: 140 } }}
              >
                <Button
                  fullWidth
                  variant="text"
                  startIcon={<DeleteOutline />}
                  onClick={handleDeleteFilter}
                  sx={{
                    justifyContent: "flex-start",
                    textTransform: "none",
                    color: "black",
                    fontWeight: "400",
                    "&:hover": { color: "red", backgroundColor: "transparent" },
                  }}
                >
                  Delete Filter
                </Button>
              </Popover>

              {/* ── Sort panel Popover — anchored to chip row Box ─────────────── */}
              {sortRules?.length > 0 && (
                <Popover
                  open={sortOpen}
                  anchorEl={sortAnchorEl}
                  onClose={handleSortPanelClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  disableScrollLock
                  PaperProps={{
                    sx: {
                      mt: 0.5,
                      width: 380,
                      borderRadius: 2,
                      p: 0,
                      overflow: "hidden",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                    },
                  }}
                >
                  {/* Header */}
                  <Box
                    sx={{
                      px: 2,
                      py: 1.2,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      backgroundColor: "background.paper",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <SwapVert
                        sx={{ fontSize: 16, color: "text.secondary" }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Sort
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={handleSortPanelClose}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>

                  <Box sx={{ p: 1.5 }}>
                    {sortRules.map((rule, index) => (
                      <Box
                        key={rule.key}
                        draggable={!serverTable}
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragEnd={handleDragEnd}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                          px: 0.5,
                          py: 0.5,
                          borderRadius: 1.5,
                          cursor: serverTable ? "default" : "grab",
                          border: "1px solid",
                          borderColor:
                            dragIndex === index ? "warning.main" : "divider",
                          backgroundColor:
                            dragIndex === index
                              ? "action.selected"
                              : "background.paper",
                          transition: "all 0.15s ease",
                          "&:hover": { backgroundColor: "action.hover" },
                        }}
                      >
                        {!serverTable && <DragIndicator
                          fontSize="small"
                          sx={{
                            color: "text.disabled",
                            cursor: "grab",
                            flexShrink: 0,
                          }}
                        />}
                        {serverTable ? <Select
                          size="small"
                          value={rule.key}
                          inputProps={{ "aria-label": "Sort field" }}
                          onChange={event => setSortDirection(event.target.value, rule.direction)}
                          sx={{ flex: 1, minWidth: 0, height: 32, fontSize: 13 }}
                        >
                          {addSortHeaders.map(header => <MenuItem key={header.key} value={header.key}>{header.name}</MenuItem>)}
                        </Select> : <Typography
                          variant="body2"
                          sx={{
                            flex: 1,
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: 13,
                          }}
                        >
                          {rule.label}
                        </Typography>}
                        <Select
                          size="small"
                          value={rule.direction}
                          inputProps={{ "aria-label": "Sort direction" }}
                          onChange={(e) =>
                            setSortDirection(rule.key, e.target.value)
                          }
                          variant="outlined"
                          sx={{
                            minWidth: 140,
                            height: 32,
                            fontSize: 13,
                            "& .MuiSelect-select": { py: "4px" },
                          }}
                        >
                          <MenuItem value="asc">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                fontSize: 13,
                              }}
                            >
                              <ArrowUpward sx={{ fontSize: 14 }} /> Ascending
                            </Box>
                          </MenuItem>
                          <MenuItem value="desc">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                fontSize: 13,
                              }}
                            >
                              <ArrowDownward sx={{ fontSize: 14 }} /> Descending
                            </Box>
                          </MenuItem>
                        </Select>
                        <IconButton
                          size="small"
                          onClick={() => {
                            removeSortRule(rule.key);
                            if (serverTable) handleSortPanelClose();
                          }}
                          sx={{
                            color: "text.secondary",
                            flexShrink: 0,
                            "&:hover": { color: "error.main" },
                          }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}

                    <Divider sx={{ my: 1 }} />

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Button
                        size="small"
                        startIcon={<Add sx={{ fontSize: 14 }} />}
                        onClick={handleOpenAddSortMenu}
                        disabled={availableSortHeaders.length === 0 || (Boolean(serverTable) && sortRules.length >= 1)}
                        sx={{
                          textTransform: "none",
                          color: "text.primary",
                          fontWeight: 400,
                          fontSize: 13,
                          px: 1,
                        }}
                      >
                        Add sort
                      </Button>
                      {sortRules.length > 0 && (
                        <Button
                          size="small"
                          startIcon={<DeleteOutline sx={{ fontSize: 14 }} />}
                          onClick={() => {
                            sortRules.forEach((r) => removeSortRule(r.key));
                            if (serverTable) handleSortPanelClose();
                          }}
                          sx={{
                            textTransform: "none",
                            color: "text.secondary",
                            fontWeight: 400,
                            fontSize: 13,
                            px: 1,
                            "&:hover": { color: "error.main" },
                          }}
                        >
                          Delete sort
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Popover>
              )}

              {/* ── Add Sort field picker ─────────────────────────────────── */}
              <Popover
                open={openAddSort}
                anchorEl={addSortMenuAnchor}
                onClose={handleCloseAddSortMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                PaperProps={{ sx: { mt: 0.5, width: 230, borderRadius: 2 } }}
              >
                <Box sx={{ p: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search for a property..."
                    value={sortSearchQuery}
                    onChange={(e) => setSortSearchQuery(e.target.value)}
                    variant="outlined"
                    autoFocus
                  />
                </Box>
                <List
                  dense
                  sx={{ pt: 0, pb: 0.7, overflowY: "auto", maxHeight: 220 }}
                >
                  {availableSortHeaders
                    .filter((h) =>
                      h?.name
                        ?.toLowerCase()
                        .includes(sortSearchQuery.toLowerCase()),
                    )
                    .map((h, i) => (
                      <ListItemButton
                        key={i}
                        onClick={() => {
                          addSortRule(h);
                          handleCloseAddSortMenu();
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {getHeaderOptionIcon(h.icon)}
                        </ListItemIcon>
                        <ListItemText
                          primary={h.name || "Unnamed"}
                          primaryTypographyProps={{ fontSize: 14 }}
                        />
                      </ListItemButton>
                    ))}
                  {availableSortHeaders.filter((h) =>
                    h?.name
                      ?.toLowerCase()
                      .includes(sortSearchQuery.toLowerCase()),
                  ).length === 0 && (
                    <Typography
                      variant="body2"
                      sx={{ textAlign: "center", color: "#777", py: 1 }}
                    >
                      No results
                    </Typography>
                  )}
                </List>
              </Popover>
            </Box>
          )}

          {/* ── Reset / Save ─────────────────────────────────────────────── */}
          {hasActiveFiltersOrSort && showSaveButton && showChip && (
            <Box sx={{ display: "flex", gap: 1, mr: 0.8, flexShrink: 0 }}>
              <Button
                color="inherit"
                size="small"
                disabled={Boolean(isSavingPreferences)}
                onClick={() => {
                  if (!screenAccess) {
                    showToast(
                      "error",
                      "Access denied: You are not authorized to reset",
                    );
                    return;
                  }
                  handleResetFilters();
                }}
              >
                Clear
              </Button>
              <Button
                variant="contained"
                size="small"
                color="warning"
                disabled={Boolean(isSavingPreferences)}
                onClick={() => {
                  if (!screenAccess) {
                    showToast(
                      "error",
                      "Access denied: You are not authorized to save",
                    );
                    return;
                  }
                  if (hasActiveFiltersOrSort) (savePreferences || updateFilterValues)();
                }}
              >
                Save
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};
